/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { ContactPerson, ActivityStatus, Lookup, Campaign, CampaignActivity } from '../../api/types/types';
import { selectCurrentFarmerAccount, selectUserContactDetails } from '../../../azure-b2c/storage';
import { useCreateActivity } from '../../api/hooks/useActivities';
import { toast } from 'react-toastify';
import { getStringFromArray } from '../../../../util/formatting';
import CreateOrUpdateActivityModal, {
  CreateOrEditFormValues,
} from '../CreateOrUpdateActivityModal/CreateOrUpdateActivityModal';
import {
  DEFAULT_ACTIVITY_DURATION,
  mapBaseActivityInputsToPayload,
} from '../../utils/mapBaseActivityInputsToPayload';
export interface CreateActivityModalProps {
  open: boolean;
  onClose: () => void;
  contactPersons?: ContactPerson[];
  campaigns?: Campaign[];
  lookups?: Lookup[];
}

const CreateActivityModal: FC<CreateActivityModalProps> = ({
  open,
  onClose,
  contactPersons = [],
  campaigns = [],
  lookups = [],
}): JSX.Element => {
  const currentFarmerAccountNumber = useSelector(selectCurrentFarmerAccount)?.accountNumber ?? '';
  const userDetails = useSelector(selectUserContactDetails);
  const currentUserName = getStringFromArray([userDetails?.firstName, userDetails?.lastName]);
  const {
    mutateAsync: createActivity,
    isPending: isCreating,
    isError: isCreateError,
  } = useCreateActivity(currentFarmerAccountNumber ?? '');

  const primaryContactId =
    contactPersons.find((contact) => contact.isPrimaryContact)?.contactId ?? '';

  const {
    control,
    handleSubmit,
    reset,
    trigger: validateActivityForm,
    getValues,
  } = useForm<CreateOrEditFormValues>({
    defaultValues: {
      activityStatus: ActivityStatus.Open,
      activityType: '',
      subject: '',
      description: '',
      scheduleDate: undefined,
      contactId: primaryContactId,
    },
    mode: 'onSubmit',
  });

  const handleCreateActivity = async (
    values: CreateOrEditFormValues,
    campaignResponses?: CampaignActivity[],
  ): Promise<void> => {
    if (currentFarmerAccountNumber) {
      await createActivity({
        ...mapBaseActivityInputsToPayload(currentFarmerAccountNumber, values),
        duration: DEFAULT_ACTIVITY_DURATION,
        ...(campaignResponses?.length ? { campaignResponses } : {}),
      });
      reset();
      toast.success(`Activity ${values.activityStatus === ActivityStatus.Completed ? 'completed' : 'created successfully'}`);
      onClose();
    }
  };

  const handleClose = (): void => {
    if (!isCreating) {
      reset();
      onClose();
    }
  };

  const activityBarProps = {
    loadingIndicatorText: 'Creating activity',
    closeButtonText: 'Cancel',
    submitButtonText: 'Create Activity',
    onClose: handleClose,
    onSubmit: handleSubmit((values) => handleCreateActivity(values)),
    showLoadingIndicator: isCreating,
  };

  return (
    <CreateOrUpdateActivityModal
      open={open}
      handleClose={onClose}
      activityBarProps={activityBarProps}
      control={control}
      validateActivityForm={validateActivityForm}
      title={'Create New Activity'}
      subtitle={'Create a new activity to track your interactions with customers.'}
      error={isCreateError ? 'Failed to create activity. Please try again.' : undefined}
      owner={currentUserName}
      contactPersons={contactPersons}
      campaigns={campaigns}
      lookups={lookups}
      onStep2Complete={(campaignResponses) => handleCreateActivity(getValues(), campaignResponses)}
    />
  );
};

export default CreateActivityModal;
